import type { QueryArgFrom, ResultTypeFrom } from '../rtk-query-types';
import type { AnyApi } from '../buildCreateApi';
import type { GetStoreFn } from '../types';
import type { AnyMutationDefinition, AnyEndpointMutation } from './types';

/**
 * Набор дополнительных методов эндпоинта, которые добавляются в RTK Query.
 * @typeParam Definition - Описание эндпоинта
 */
export type EndpointMutate<Definition extends AnyMutationDefinition> = {
    /**
     * Метод для выполнения мутации.
     */
    mutate: (queryArgs: QueryArgFrom<Definition>, options?: MutationOptions) => Promise<ResultTypeFrom<Definition>>;
};

/**
 * Параметры для `buildEndpointMutate()`.
 */
type BuildMutateOptions = {
    api: AnyApi;
    endpointName: string;
    getStore: GetStoreFn;
};

/**
 * Параметры запроса endpoint.mutate(arg, options)
 */
type MutationOptions = {
    /**
     * Ключ для объединения результатов мутации с хуками useMutation(),
     * которые имеют такой же ключ в параметрах.
     */
    fixedCacheKey?: string;
};

export function buildEndpointMutate({
    api,
    endpointName,
    getStore,
}: BuildMutateOptions): EndpointMutate<AnyMutationDefinition>['mutate'] {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- DIRECT-239681
    const endpoint = api.endpoints[endpointName] as AnyEndpointMutation;

    return function mutate(queryArgs, options) {
        const fixedCacheKey = options?.fixedCacheKey;
        const shouldTrack = fixedCacheKey !== undefined;

        const promise = getStore().dispatch(
            endpoint.initiate(queryArgs, {
                fixedCacheKey,
                track: shouldTrack,
            })
        );

        promise.then(() => {
            // После завешения мутации удаляем результат из стора, иначе он останется там навсегда
            promise.reset();
        });

        return promise.unwrap();
    };
}
